import { formatTimestamp } from '@/utils/dateFormatters'
import { type FieldDiff, formatDiffValue } from '@/utils/objectDiff'
import { getInitials } from '@/utils/stringFormatters'
import { ArrowRightOutlined, UndoOutlined } from '@ant-design/icons'
import {
  GraviButton,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Avatar, Card, Popconfirm, Tag, Tooltip, theme } from 'antd'
import type { HistoryEntry } from './historyEntries'

/** Longer values are elided in the row and shown in full on hover. */
const MAX_VALUE_CHARS = 120

/* GraviButton maps appearance='outline' onto antd's `filled` variant, whose
   tint reads as a solid block here, so the pill border is set explicitly. */
/* Without a cap the confirm stretches to fit the description on one line,
   spanning most of the drawer. */
const CONFIRM_STYLES = { root: { maxWidth: 360 } }

/* Notes are free text of any length, so the tag gets its own row and wraps
   rather than squeezing the author and timestamp beside it. */
const NOTE_STYLE = {
  marginInlineEnd: 0,
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
}

const RESTORE_STYLE = {
  color: 'hsl(var(--primary))',
  border: '1px solid hsl(var(--primary))',
  borderRadius: 999,
}

interface ValueTextProps {
  value: unknown
  color: string
}

function ValueText({ value, color }: ValueTextProps) {
  const text = formatDiffValue(value)
  const truncated =
    text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text

  const content = (
    <Texto style={{ fontSize: 12, color, wordBreak: 'break-word' }}>
      {truncated}
    </Texto>
  )

  return truncated === text ? (
    content
  ) : (
    <Tooltip title={text}>{content}</Tooltip>
  )
}

function ChangeRow({ change }: { change: FieldDiff }) {
  const { token } = theme.useToken()
  const afterColor =
    change.kind === 'removed' ? token.colorTextTertiary : token.colorText

  return (
    <Horizontal gap={8} alignItems='baseline' style={{ flexWrap: 'wrap' }}>
      <Texto weight='600' style={{ fontSize: 12, flexShrink: 0 }}>
        {change.label}
      </Texto>
      <Horizontal gap={6} alignItems='baseline' style={{ minWidth: 0 }}>
        <ValueText value={change.before} color={token.colorTextTertiary} />
        <ArrowRightOutlined
          style={{ fontSize: 10, color: token.colorTextTertiary }}
        />
        <ValueText value={change.after} color={afterColor} />
      </Horizontal>
    </Horizontal>
  )
}

interface HistoryEntryCardProps {
  entry: HistoryEntry
  onRestore?: (auditId: string) => void
  restoring?: boolean
}

export function HistoryEntryCard({
  entry,
  onRestore,
  restoring,
}: HistoryEntryCardProps) {
  const { token } = theme.useToken()
  const { audit, changes, comparedTo, restoreAuditId } = entry
  const updatedBy = audit.updated_by ?? 'System'

  return (
    <Horizontal gap={12} style={{ position: 'relative' }}>
      {/* Timeline dot */}
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: token.colorInfo,
          flexShrink: 0,
          marginTop: 18,
          zIndex: 1,
        }}
      />

      <Card size='small' style={{ flex: 1 }} styles={{ body: { padding: 12 } }}>
        <Vertical gap={8}>
          <Horizontal justifyContent='space-between' alignItems='center'>
            <Horizontal gap={8} alignItems='center'>
              <Avatar
                size={20}
                style={{
                  backgroundColor: 'var(--gray-400)',
                  fontSize: 10,
                  flexShrink: 0,
                }}
              >
                {getInitials(updatedBy)}
              </Avatar>
              <Texto weight='600'>{updatedBy}</Texto>
            </Horizontal>
            <Horizontal gap={8} alignItems='center'>
              <Texto
                appearance='hint'
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              >
                {formatTimestamp(audit.updated_on)}
              </Texto>
              {/* No button on the newest entry: its result is the live
                  document, so there is nothing to restore. */}
              {onRestore && restoreAuditId && (
                <Popconfirm
                  title='Roll back to this snapshot?'
                  description='The data will be reset to the values it had when this snapshot was taken. All changes after this point will be undone.'
                  okText='Restore'
                  cancelText='Cancel'
                  placement='bottomRight'
                  styles={CONFIRM_STYLES}
                  onConfirm={() => onRestore(restoreAuditId)}
                >
                  <GraviButton
                    size='small'
                    appearance='text'
                    buttonText='Restore'
                    icon={<UndoOutlined />}
                    loading={restoring}
                    style={RESTORE_STYLE}
                  />
                </Popconfirm>
              )}
            </Horizontal>
          </Horizontal>

          {audit.note && (
            <div>
              <Tag bordered={false} style={NOTE_STYLE}>
                {audit.note}
              </Tag>
            </div>
          )}

          {changes.length > 0 ? (
            <Vertical gap={6}>
              {changes.map((change) => (
                <ChangeRow key={change.path} change={change} />
              ))}
            </Vertical>
          ) : (
            <Texto appearance='hint' style={{ fontSize: 12 }}>
              {comparedTo === null
                ? 'Nothing to compare against — the current document was not supplied.'
                : 'No field changes.'}
            </Texto>
          )}
        </Vertical>
      </Card>
    </Horizontal>
  )
}
